import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Shuffle, ChevronLeft, ChevronRight, Maximize2, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const CATEGORY_COLORS = {
  core: { bg: 'bg-yellow-500', text: 'text-white', name: 'You at the Core', light: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' },
  health: { bg: 'bg-green-600', text: 'text-white', name: 'Health', light: 'bg-green-500/10 text-green-600 border-green-500/30' },
  business: { bg: 'bg-blue-600', text: 'text-white', name: 'Business', light: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  family: { bg: 'bg-orange-500', text: 'text-white', name: 'Family', light: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
  curiosities: { bg: 'bg-purple-600', text: 'text-white', name: 'Curiosities', light: 'bg-purple-500/10 text-purple-600 border-purple-500/30' },
};

export default function CardsTab({ meetingId }) {
  const { api, isChair } = useAuth();
  const [cards, setCards] = useState([]);
  const [usedCardIds, setUsedCardIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filter, setFilter] = useState('all');
  const [direction, setDirection] = useState(0);
  const [fullscreenCard, setFullscreenCard] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, usageRes] = await Promise.all([
        api().get('/cards'),
        api().get(`/meetings/${meetingId}/cards/used`),
      ]);
      setCards(cardsRes.data);
      setUsedCardIds(new Set(usageRes.data.map(u => u.card_id)));
    } catch (error) {
      console.error('Failed to fetch cards:', error);
      toast.error('Failed to load cards');
    } finally {
      setLoading(false);
    }
  }, [api, meetingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredCards = useMemo(() => {
    let result = cards;
    if (filter !== 'all') {
      result = result.filter(card => card.category === filter);
    }
    return result;
  }, [cards, filter]);

  const currentCard = filteredCards[currentIndex];

  const handleNext = () => {
    if (currentIndex < filteredCards.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleShuffle = () => {
    const unusedCards = filteredCards.filter(card => !usedCardIds.has(card.id));
    if (unusedCards.length === 0) {
      toast.info('All cards have been used');
      return;
    }
    const randomCard = unusedCards[Math.floor(Math.random() * unusedCards.length)];
    const newIndex = filteredCards.findIndex(card => card.id === randomCard.id);
    setDirection(newIndex > currentIndex ? 1 : -1);
    setCurrentIndex(newIndex);
    toast.success('Shuffled to a random unused card');
  };

  const handleMarkUsed = async () => {
    if (!currentCard || !isChair) return;

    const isUsed = usedCardIds.has(currentCard.id);
    
    try {
      if (isUsed) {
        await api().delete(`/meetings/${meetingId}/cards/${currentCard.id}/use`);
        setUsedCardIds(prev => {
          const next = new Set(prev);
          next.delete(currentCard.id);
          return next;
        });
        toast.success('Card unmarked');
      } else {
        await api().post(`/meetings/${meetingId}/cards/${currentCard.id}/use`);
        setUsedCardIds(prev => new Set(prev).add(currentCard.id));
        toast.success('Card marked as used');
      }
    } catch (error) {
      toast.error('Failed to update card');
    }
  };

  const handleDragEnd = (event, info) => {
    const threshold = 100;
    if (info.offset.x > threshold && currentIndex > 0) {
      handlePrev();
    } else if (info.offset.x < -threshold && currentIndex < filteredCards.length - 1) {
      handleNext();
    }
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cards-tab">
      {/* Filter and stats */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="card-filter-select">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_COLORS).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  {value.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Badge variant="outline" className="text-sm">
            {usedCardIds.size} / {cards.length} used
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleShuffle}
            className="gap-2"
            data-testid="shuffle-btn"
          >
            <Shuffle className="h-4 w-4" />
            Random
          </Button>
        </div>
      </div>

      {filteredCards.length === 0 ? (
        <Card className="p-12">
          <div className="empty-state">
            <p className="text-muted-foreground">No cards found for this category.</p>
          </div>
        </Card>
      ) : (
        <>
          {/* Card deck */}
          <div className="relative h-[420px] md:h-[480px] flex items-center justify-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentCard?.id}
                custom={direction}
                initial={{ 
                  opacity: 0, 
                  x: direction * 300,
                  rotateY: direction * 15 
                }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  rotateY: 0 
                }}
                exit={{ 
                  opacity: 0, 
                  x: direction * -300,
                  rotateY: direction * -15 
                }}
                transition={{ 
                  type: 'spring', 
                  stiffness: 300, 
                  damping: 30 
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.5}
                onDragEnd={handleDragEnd}
                className="absolute w-full max-w-sm cursor-grab active:cursor-grabbing"
                data-testid="current-card"
              >
                <Card className={`overflow-hidden shadow-2xl ${usedCardIds.has(currentCard?.id) ? 'ring-2 ring-green-500' : ''}`}>
                  {/* Category header */}
                  <div className={`${CATEGORY_COLORS[currentCard?.category]?.bg} ${CATEGORY_COLORS[currentCard?.category]?.text} px-4 py-3 flex items-center justify-between`}>
                    <span className="font-semibold text-sm uppercase tracking-wider">
                      {CATEGORY_COLORS[currentCard?.category]?.name}
                    </span>
                    {usedCardIds.has(currentCard?.id) && (
                      <Badge className="bg-white/20 text-white border-white/30">
                        <Check className="h-3 w-3 mr-1" />
                        Used
                      </Badge>
                    )}
                  </div>
                  
                  {/* Card content */}
                  <CardContent className="p-6 min-h-[280px] flex flex-col justify-center">
                    <p className="text-xl md:text-2xl leading-relaxed font-medium text-center">
                      {currentCard?.prompt_text}
                    </p>
                  </CardContent>

                  {/* Actions */}
                  <div className="px-4 py-3 border-t flex items-center justify-between bg-muted/30">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFullscreenCard(currentCard)}
                      data-testid="fullscreen-card-btn"
                    >
                      <Maximize2 className="h-4 w-4 mr-1" />
                      Present
                    </Button>
                    
                    {isChair && (
                      <Button
                        variant={usedCardIds.has(currentCard?.id) ? "secondary" : "default"}
                        size="sm"
                        onClick={handleMarkUsed}
                        className="gap-1"
                        data-testid="mark-used-btn"
                      >
                        {usedCardIds.has(currentCard?.id) ? (
                          <>
                            <RotateCcw className="h-4 w-4" />
                            Unmark
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            Mark Used
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              data-testid="prev-card-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            
            <span className="text-sm text-muted-foreground min-w-[80px] text-center font-mono">
              {currentIndex + 1} / {filteredCards.length}
            </span>
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleNext}
              disabled={currentIndex === filteredCards.length - 1}
              data-testid="next-card-btn"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Category indicators */}
          <div className="flex justify-center gap-1 flex-wrap">
            {Object.entries(CATEGORY_COLORS).map(([key, value]) => {
              const count = cards.filter(c => c.category === key).length;
              const usedCount = cards.filter(c => c.category === key && usedCardIds.has(c.id)).length;
              return (
                <Badge 
                  key={key}
                  variant="outline"
                  className={`${value.light} cursor-pointer`}
                  onClick={() => setFilter(key)}
                >
                  {value.name}: {usedCount}/{count}
                </Badge>
              );
            })}
          </div>
        </>
      )}

      {/* Fullscreen dialog */}
      <Dialog open={!!fullscreenCard} onOpenChange={() => setFullscreenCard(null)}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle className={`${CATEGORY_COLORS[fullscreenCard?.category]?.text} ${CATEGORY_COLORS[fullscreenCard?.category]?.bg} -mx-6 -mt-6 px-6 py-4 rounded-t-lg`}>
              {CATEGORY_COLORS[fullscreenCard?.category]?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 flex items-center justify-center p-8">
            <p className="text-3xl md:text-4xl lg:text-5xl leading-relaxed font-medium text-center">
              {fullscreenCard?.prompt_text}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
